import { withPrefix } from 'gatsby'
import PropTypes from 'prop-types'
import React from 'react'
import ReactPlayer from 'react-player'
import styled from 'styled-components'
import Article from '../templates/Article'

const Page = props => {
  return (
    <Article pathName={props.location.pathname} pageTitle="Book Review Podcast">
      <StyledPlayer>
        <div>
          <ReactPlayer
            controls={true}
            height="48px"
            width="100%"
            url={withPrefix('/dswmedia/eme5054-podcast_mixdown.mp3')}
          />
        </div>
      </StyledPlayer>
      <section aria-labelledby="section-book-info">
        <div className="contain">
          <StyledIntro>
            <h1
              css={css`
                padding-top: 0 !important;
              `}
            >
              Book Review Podcast
            </h1>
            <p>
              <a
                href="https://www.goodreads.com/book/show/35192160-out-of-our-minds?ac=1&from_search=true&qid=ABescLEKnG&rank=2"
                target="_blank"
                rel="noopener noreferrer"
              >
                Out of Our Minds, The Power of Being Creative
              </a>
              <br />
              by Sir Ken Robinson
            </p>
            <img
              css={css`
                max-width: 300px;
              `}
              src="https://i.gr-assets.com/images/S/compressed.photo.goodreads.com/books/1497299747l/35192160.jpg"
              alt="Out of Our Minds front cover"
            />
          </StyledIntro>
        </div>
      </section>
      <StyledTranscript id="transcript">
        <div className="contain">
          <h2>Transcript</h2>
          <p>
            Hi there, my name is Phil Sinatra and I’m reviewing{' '}
            <em>Out of Our Minds, The Power of Being Creative</em> by Sir Ken
            Robinson. He's an internationally recognized leader in the
            development of creativity, innovation, and human resources.
            Originally born in 1950 in Liverpool, England, Sir Ken completed a
            PhD at the University of London in 1981 and worked as a professor of
            education at the University of Warwick in the UK for twelve years.
            He has received numerous honorary degrees and awards for his work in
            education and cultural development, and is the most watched speaker
            in TED talk’s history. Along with this book, he has authored two New
            York Times bestsellers, and has been called one of “the world’s
            elite thinkers on creativity and innovation”. Sir Ken past away in
            this past August (2020) after a short battle with cancer.
          </p>
          <p>
            The book sets out to answer three questions:
            <a
              href="#fn1"
              className="footnote-ref"
              id="fnref1"
              role="doc-noteref"
            >
              <sup>1</sup>
            </a>
          </p>
          <ol type="1">
            <li>Why is it essential to promote creativity?</li>
            <li>What is the problem?</li>
            <li>What is involved?</li>
          </ol>
          <p>
            To answer the question of why is it essential to promote creativity,
            Sir Ken calls on career experience where he repeatedly heard from
            business owners that education systems are not producing the types
            of employees they need. Educators blame politicians who impose a
            culture of standardization and testing designed to work in the
            interests of the economy. “There is hardly a school system in the
            world that teaches dance every day as a compulsory discipline in the
            same way mathematics is taught”.
            <a
              href="#fn2"
              className="footnote-ref"
              id="fnref2"
              role="doc-noteref"
            >
              <sup>2</sup>
            </a>{' '}
            He feels is it essential to promote, rather than stifle creativity
            in the education system, and to not force everyone into the same
            educational hierarchy.
          </p>
          <p>
            This leads into the next question, which is defining the problem.
            “The assumption of a direct line between what is taught in school
            and the work that young people do afterwards puts priority on
            subjects that seem most relevant to the economy. If the economy
            needs more scientists and technologists, science and technology are
            given higher priority and other programs, in the arts and
            humanities, for example, are cut to make way for them. This policy
            is not in the better interests of young people or of society in
            general and it is not even the best way to produce good scientists
            and technologists.”.
            <a
              href="#fn3"
              className="footnote-ref"
              id="fnref3"
              role="doc-noteref"
            >
              <sup>3</sup>
            </a>
          </p>
          <p>
            Sir Ken spends some time defining the relationship between thinking
            and feeling, and how that concept is at the heart of creativity.
            “Many scientists have a deep interest in the arts, and a growing
            number of artists take inspiration from scientific ideas”.
            <a
              href="#fn4"
              className="footnote-ref"
              id="fnref4"
              role="doc-noteref"
            >
              <sup>4</sup>
            </a>{' '}
            He gives a few examples where mixing scientific concepts with
            artistic expressions have fostered incredible results, including an
            example of a collaboration between fashion design and biological
            sciences. I immediately thought of an article a read recently about
            a band. In the article, the members of the band detailed how they
            developed the melody and lyrics of a song based on the Fibonacci
            sequence.
          </p>
          <p>
            (music excerpt:{' '}
            <a
              href="https://open.spotify.com/track/7tvuLLroI0n6uYBWuFig5d?si=VEpIujjJSgCYyPcM9ckDPg"
              target="_blank"
              rel="noopener noreferrer"
            >
              “Lateralus” by TOOL
            </a>
            )
          </p>
          <p>
            A few months ago I read a different book titled “Prepared, What Kids
            Need for a Fulfilled Life” by Diane Tavenner. Diane is an educator
            and co-founder of the Summit Public School system, which is a
            charter school that aims to prepare students not only for college,
            but also for life beyond formal education. The core philosophies in
            the school include self-directed learning, project-based learning
            and a mentoring system, where students are given freedom to pursue
            projects and ideas that they find interest in. Students are
            encouraged to collaborate and problem solve real life problems from
            the earliest stages of their education. The system treats education
            as an adventure, rather than a twelve year todo list of things that
            must be completed to satisfy a standardized set of requirements.
          </p>
          <p>
            The philosophy outlined by the Summit school system aligns with the
            concepts Sir Ken presents in <em>Out of Our Minds</em>. “In the
            interests of industrial economics, we have subjected generations of
            people to narrow forms of education that have marginalized some of
            their most important talents and qualities. In pursuit of higher
            levels of productivity, we have overlooked the essential human
            factors on which creativity and innovation naturally depend.”{' '}
            <a
              href="#fn5"
              className="footnote-ref"
              id="fnref5"
              role="doc-noteref"
            >
              <sup>5</sup>
            </a>
          </p>
          <p>
            This book is a great read that for once not only defines a problem,
            but actually offers a solution. I highly recommend it, you won’t be
            disappointed. Thank you for listening.
          </p>
          <h2 id="footnotes">Footnotes</h2>
          <ul>
            <li id="fn1" role="doc-endnote">
              <p>
                [1] Robinson, Ken, “Out of Our Minds, The Power of Being
                Creative” November 2017, 2
                <a href="#fnref1" className="footnote-back" role="doc-backlink">
                  ↩︎
                </a>
              </p>
            </li>
            <li id="fn2" role="doc-endnote">
              <p>
                [2] Robinson, Ken, “Out of Our Minds, The Power of Being
                Creative” November 2017, 183
                <a href="#fnref2" className="footnote-back" role="doc-backlink">
                  ↩︎
                </a>
              </p>
            </li>
            <li id="fn3" role="doc-endnote">
              <p>
                [3] Robinson, Ken, “Out of Our Minds, The Power of Being
                Creative” November 2017, 48
                <a href="#fnref3" className="footnote-back" role="doc-backlink">
                  ↩︎
                </a>
              </p>
            </li>
            <li id="fn4" role="doc-endnote">
              <p>
                [4] Robinson, Ken, “Out of Our Minds, The Power of Being
                Creative” November 2017, 183
                <a href="#fnref4" className="footnote-back" role="doc-backlink">
                  ↩︎
                </a>
              </p>
            </li>
            <li id="fn5" role="doc-endnote">
              <p>
                [5] Robinson, Ken, “Out of Our Minds, The Power of Being
                Creative” November 2017, 239
                <a href="#fnref5" className="footnote-back" role="doc-backlink">
                  ↩︎
                </a>
              </p>
            </li>
          </ul>
          <h2 id="additional-resources">Additional Resources</h2>
          <ul>
            <li>
              Fera, Rae Ann. “Ken Robinson On The Principles Of Creative
              Leadership” <em>Fast Company</em>,{' '}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.fastcompany.com/1764044/ken-robinson-principles-creative-leadership"
              >
                https://www.fastcompany.com/1764044/ken-robinson-principles-creative-leadership
              </a>
              . Accessed 18 September 2020.
            </li>
            <li>
              “Ken Robinson (educationalist)” <em>Wikipedia</em>,{' '}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://en.wikipedia.org/wiki/Ken_Robinson_(educationalist)"
              >
                https://tinyurl.com/vvo4gu6
              </a>
              . Accessed 18 September 2020
            </li>
            <li>
              Tavenner, Diane. Prepared, What Kids Need for a Fulfilled Life,
              New York, Currency Books, September 2019
            </li>
            <li>
              TOOL. “Lateralus.” Produced by David Bottrill, Hollywood,
              California, 2001.
            </li>
            <li>
              Intro music by{' '}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://pixabay.com/users/zen_man-4257870/?tab=audio&amp;utm_source=link-attribution&amp;utm_medium=referral&amp;utm_campaign=audio&amp;utm_content=790"
              >
                Zen_Man
              </a>{' '}
              from{' '}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://pixabay.com/music/search/genre/intro%2Foutro/"
              >
                Pixabay
              </a>
            </li>
          </ul>
        </div>
      </StyledTranscript>
    </Article>
  )
}

Page.propTypes = {
  location: PropTypes.object,
}

const StyledPlayer = styled.div`
  align-items: center;
  background-color: var(--color-gray-900);
  border-bottom: 1px solid var(--color-gray-800);
  display: flex;
  height: 100px;
  justify-content: center;
  position: fixed;
  top: 48px;
  width: 100%;
  z-index: 40;

  > div {
    margin: 0 auto;
    max-width: 600px;
    width: 90%;
  }
`

const StyledIntro = styled.div`
  margin-top: 11.25rem;
  text-align: center;
`

const StyledTranscript = styled.section`
  font-size: 85%;
  font-weight: normal;
  line-height: 1.2rem;
  margin: 3rem auto;
  overflow-x: hidden;

  h2 {
    font-weight: bold;
    font-size: 0.875rem;
    text-align: center;
    text-transform: uppercase;
  }

  li p {
    margin: 0;
  }

  ul {
    padding-left: 1rem;
  }
`

export default Page
